import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { signOut } from '../lib/auth.js';
import { useApi, useAction, useAuth } from '../lib/hooks.js';
import { Loading, ErrorNote, Modal, Pill, Spinner, todayLocal } from '../components/ui.jsx';
import { THEMES, MODES, readTheme, readMode, applyTheme } from '../lib/theme.js';
import { readReferral, clearReferral } from '../lib/referral.js';
import { runningInPlayApp } from '../lib/client.js';

export default function Settings() {
  const health = useApi('/health');

  return (
    <div className="stack-lg">
      <AccountSettings />

      <HouseholdSettings />

      <PlanSettings />

      <ReferSettings />

      <AppearanceSettings />

      <PrivacySettings />

      <HelpSettings />

      <section>
        <div className="section-title"><h2>Status</h2></div>
        <div className="card list">
          <div className="list-item">
            <span className="grow">Photo analysis</span>
            {health.data?.vision
              ? <Pill tone="ok">{health.data.visionModel}</Pill>
              : <Pill tone="warn">no API key</Pill>}
          </div>
          <div className="list-item">
            <span className="grow">Nutrition source</span>
            <Pill>{health.data?.nutritionProvider}</Pill>
          </div>
          {/* "can it try", not "will it arrive". A provider that accepts the
              credentials and then refuses the message is something only the
              attempt finds out, and the invite screen says so when it does. */}
          <div className="list-item">
            <span className="grow">Invitation email</span>
            {health.data?.email
              ? <Pill tone="ok">can send</Pill>
              : <Pill tone="warn">not configured</Pill>}
          </div>
        </div>
        {health.data && !health.data.vision && (
          <p className="tiny muted" style={{ marginTop: 8 }}>
            Set <code>ANTHROPIC_API_KEY</code> in <code>.env</code> and restart the server to enable
            receipt scanning, shelf photos, and recipe photo import.
          </p>
        )}
        {health.data && !health.data.email && (
          <p className="tiny muted" style={{ marginTop: 8 }}>
            Invitations still work &mdash; the link and the code are the invitation, and sending
            them by hand always has. Set the Supabase URL and service key to have them emailed too.
          </p>
        )}
      </section>

      <LocationSettings />
      <ImportSettings />
      <RecipeSourceSettings />
    </div>
  );
}

/**
 * Who is signed in, and the way back out.
 *
 * Renders nothing when the deployment does not require sign-in — a local run
 * has no account to show and nothing to sign out of.
 */
/**
 * The plan, what it allows, and how to change it.
 *
 * Only model calls are capped, so this deliberately says "photo scans" rather
 * than anything resembling a general usage meter -- everything else in the app
 * is unlimited on both plans and saying otherwise would be a lie that costs
 * conversions.
 */
function PlanSettings() {
  const usage = useApi('/usage');
  const health = useApi('/health');
  const { busy, error, setError, run } = useAction();
  const [notice, setNotice] = useState(() => {
    // Stripe sends the browser back here. Read it once, then clear it from the
    // URL so a refresh does not re-announce a purchase.
    const result = new URLSearchParams(window.location.search).get('checkout');
    if (result) window.history.replaceState({}, '', window.location.pathname);
    return result;
  });

  const billingOff = health.data && health.data.billing === false;
  const plan = usage.data;
  // Read once per render rather than per branch: whether this is the Play app
  // cannot change while the screen is open, and three calls asking the same
  // question is three chances for two of them to disagree.
  const inPlayApp = runningInPlayApp();
  // 'paid' does not mean the money is arriving. billing.js deliberately keeps a
  // past_due subscription on the paid plan while Stripe retries the card, so
  // the plan alone would have the Play app say "active" directly under a row
  // reading "payment failed — retrying". A Stripe trial is paid and fine and
  // reads as active; anything else points at that row rather than contradicting
  // it.
  const subscriptionSettled = !plan?.subscription_status
    || plan.subscription_status === 'active'
    || plan.subscription_status === 'trialing';

  const go = (path, body) => run(async () => {
    setError(null);
    const { url } = await api.post(path, body);
    // Stripe's own pages. A full navigation rather than a new tab: coming back
    // is a redirect Stripe controls, and a popup would strand it.
    window.location.assign(url);
  });

  return (
    <section>
      <div className="section-title"><h2>Plan</h2></div>

      {notice === 'done' && (
        <div className="alert ok" role="status" style={{ marginBottom: 8 }}>
          <strong>Thank you.</strong>
          <div className="small" style={{ marginTop: 4 }}>
            Your plan updates within a few seconds of Stripe confirming the payment.
            {' '}
            <button type="button" className="link" onClick={() => { setNotice(null); usage.reload(); }}>
              Check now
            </button>
          </div>
        </div>
      )}
      {notice === 'cancelled' && (
        <div className="alert" role="status" style={{ marginBottom: 8 }}>
          No charge was made. Nothing has changed.
        </div>
      )}

      {usage.loading && <Loading />}
      {usage.error && <ErrorNote error={usage.error} />}
      {error && <ErrorNote error={error} />}

      {plan && (
        <div className="card list">
          <div className="list-item">
            <span className="grow">Current plan</span>
            {plan.trialing
              ? <Pill tone="ok">Trial &middot; {plan.trial_days_left} day{plan.trial_days_left === 1 ? '' : 's'} left</Pill>
              : plan.plan === 'paid'
                ? <Pill tone="ok">Paid</Pill>
                : <Pill>Free</Pill>}
          </div>
          <div className="list-item">
            <span className="grow">Photo scans this month</span>
            <span className="mono small">{plan.used} of {plan.limit}</span>
          </div>
          {/* Its own line rather than added into the number above. That one is
              the month's allowance and resets; these were earned and do not, so
              showing "5 of 25" would promise a 25 that is not there next
              month. Hidden entirely at zero, where it would only raise the
              question of what it is. */}
          {plan.reward_scans > 0 && (
            <div className="list-item">
              <span className="grow">Referral scans</span>
              <span className="mono small">{plan.reward_scans} left</span>
            </div>
          )}
          <div className="list-item">
            <span className="grow">Resets</span>
            <span className="small muted">{plan.resets_at}</span>
          </div>
          {/* Stripe's own word for what the subscription is doing. Shown only
              when it is not the ordinary case, because "active" is noise. */}
          {plan.subscription_status && plan.subscription_status !== 'active' && (
            <div className="list-item">
              <span className="grow">Subscription</span>
              <Pill tone={plan.subscription_status === 'past_due' ? 'warn' : undefined}>
                {plan.subscription_status === 'past_due' ? 'payment failed — retrying' : plan.subscription_status}
              </Pill>
            </div>
          )}
        </div>
      )}

      {billingOff && (
        <div className="alert warn" style={{ marginTop: 8 }}>
          <strong>Subscriptions are not set up on this deployment.</strong>
          <div className="small" style={{ marginTop: 4 }}>
            Everything in the app still works. The free allowance applies.
          </div>
        </div>
      )}

      {/* Inside the Play app there is no purchase card of either kind, and this
          is the whole of posture A from docs/play-listing.md section 0. An app
          distributed by Play that sells a subscription owes Play its payments
          policy: Play Billing outside the US, or the US alternative-billing
          programme with its 10% fee and its 24-hour reporting. A $5 month nets
          $4.56 after Stripe and costs about $3.96 in model calls at heavy use,
          so either fee makes the subscription lose money. Nothing is for sale
          here, so neither obligation is triggered.

          The sentence below is text and nothing else -- no anchor, no button,
          no copy-to-clipboard. Under the current US terms a neutral statement
          of where plans are managed is permitted communication, while a
          tappable link is what the External Content Links programme attaches
          to, with its own fee and its own reporting. The whole distinction is
          one HTML tag, which is why this comment is longer than the two
          paragraphs it guards. */}
      {inPlayApp && !billingOff && plan && (
        <div className="card stack" style={{ marginTop: 8 }}>
          <p className="small muted">
            {plan.plan === 'paid'
              ? (subscriptionSettled
                ? 'Your subscription is active.'
                : 'Your subscription needs attention — see the status above.')
              : plan.trialing
                ? `Your trial gives you ${plan.limit} photo scans a month. Subscribing keeps it there when the trial ends.`
                : `The free plan allows ${plan.limit} photo scans a month. Everything else — inventory, recipes, shopping lists, barcode scanning — stays unlimited either way.`}
          </p>
          <p className="small muted">
            Plans are managed at {window.location.host} in a web browser.
          </p>
        </div>
      )}

      {!inPlayApp && !billingOff && plan && plan.plan !== 'paid' && (
        <div className="card stack" style={{ marginTop: 8 }}>
          <p className="small muted">
            {plan.trialing
              ? `Your trial gives you ${plan.limit} photo scans a month. Subscribing keeps it there when the trial ends.`
              : `The free plan allows ${plan.limit} photo scans a month. Everything else — inventory, recipes, shopping lists, barcode scanning — stays unlimited either way.`}
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="primary" disabled={busy}
              onClick={() => go('/billing/checkout', { interval: 'monthly' })}>
              $5 / month
            </button>
            <button type="button" disabled={busy}
              onClick={() => go('/billing/checkout', { interval: 'annual' })}>
              $50 / year
            </button>
          </div>
          <p className="tiny muted">
            Two months free on the annual plan. Payment is handled by Stripe; no card details
            reach this app.
          </p>
        </div>
      )}

      {!inPlayApp && !billingOff && plan && plan.plan === 'paid' && (
        <div className="card stack" style={{ marginTop: 8 }}>
          <p className="small muted">
            Change your card, see past invoices, or cancel. Cancelling keeps the paid
            allowance until the period you have paid for ends, then falls back to free —
            your pantry is never locked.
          </p>
          <button type="button" disabled={busy} onClick={() => go('/billing/portal', {})}>
            Manage subscription
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Telling somebody else about the app, and being paid in scans for it.
 *
 * Beside the plan rather than beside the household invitation, which is the
 * other thing in this app that produces a code and means something completely
 * different: that one lets a person into THIS pantry. This one is a
 * recommendation to a household that will never see anything of ours, and what
 * comes back is scans.
 *
 * A link first and the code second. The realistic act is texting somebody, and
 * a link they can tap beats eight characters they have to type -- but the code
 * is there too, because the other realistic act is saying it out loud in a
 * kitchen, which is where this app is used.
 */
function ReferSettings() {
  const refer = useApi('/refer');
  const [copied, setCopied] = useState(null);
  // Pre-filled from whatever link brought them here, possibly days ago. Nobody
  // who arrived by a link should have to find it again to type it in.
  const [code, setCode] = useState(readReferral);
  const { busy, error, run } = useAction();

  const mine = refer.data;
  const link = mine ? `${window.location.origin}/?refer=${encodeURIComponent(mine.code)}` : '';

  const copy = (text, what) => async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch { /* a browser that refuses the clipboard still shows both of them */ }
  };

  const send = () => run(async () => {
    await api.post('/refer/redeem', { code });
    // Used. Leaving it behind would offer it again on every visit to a pantry
    // that can no longer take one.
    clearReferral();
    setCode('');
    refer.reload();
  });

  return (
    <section>
      <div className="section-title"><h2>Refer a friend</h2></div>

      {refer.loading && <Loading />}
      {refer.error && <ErrorNote error={refer.error} />}

      {mine && (
        <>
          <div className="card stack">
            <p className="small" style={{ marginTop: 0 }}>
              Send someone your link. When they start a pantry of their own and scan something,
              you both get {mine.reward_scans} extra photo scans &mdash; and unlike the monthly
              allowance, they stay until they are used.
            </p>
            <input className="mono" readOnly value={link} onFocus={(e) => e.target.select()} />
            <button type="button" className="full" onClick={copy(link, 'link')}>
              {copied === 'link' ? 'Copied' : 'Copy link'}
            </button>
            <p className="small muted" style={{ marginBottom: 0 }}>Or read them this code:</p>
            <strong className="code-callout" aria-label={mine.code.split('').join(' ')}>
              {mine.code}
            </strong>
          </div>

          <div className="card list" style={{ marginTop: 8 }}>
            <div className="list-item">
              <span className="grow">Pantries started with your code</span>
              <span className="mono small">{mine.referred}</span>
            </div>
            {/* The gap between these two is the honest number. A referral is
                paid when it is used, not when it is signed up for. */}
            <div className="list-item">
              <span className="grow">&hellip;that have actually been used</span>
              <span className="mono small">{mine.referred_active}</span>
            </div>
            <div className="list-item">
              <span className="grow">Scans earned</span>
              <span className="mono small">{mine.earned}</span>
            </div>
            <div className="list-item">
              <span className="grow">Left to use</span>
              <span className="mono small">{mine.balance}</span>
            </div>
          </div>

          {mine.can_redeem && (
            <div className="card stack" style={{ marginTop: 8 }}>
              <label className="small" htmlFor="refer-code" style={{ marginTop: 0 }}>
                Did somebody send you here? Enter their code and you both get
                {' '}
                {mine.reward_scans} scans.
              </label>
              <ErrorNote error={error} />
              <div className="row" style={{ gap: 8 }}>
                <input
                  id="refer-code"
                  className="mono grow"
                  value={code}
                  placeholder="ABCD-EFGH"
                  onChange={(e) => setCode(e.target.value)}
                />
                <button type="button" className="primary" disabled={busy || !code.trim()} onClick={send}>
                  Use it
                </button>
              </div>
              <p className="tiny muted" style={{ marginBottom: 0 }}>
                Usable until {mine.redeem_closes_at}. It is a thank-you to whoever told you about
                this rather than a discount &mdash; nothing about your plan changes.
              </p>
            </div>
          )}

          {mine.redeemed && (
            <p className="tiny muted" style={{ marginTop: 8 }}>
              You started with code <span className="mono">{mine.redeemed.code}</span>
              {mine.redeemed.rewarded_at
                ? `. Both pantries got ${mine.reward_scans} scans.`
                : '. The scans arrive once you have scanned something.'}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The rights the privacy policy promises, as things you can actually do.
 *
 * They were previously "write to this address", which is true and is a poor
 * answer for the two of them a machine can do in a second.
 */
/**
 * Which of the four looks the app wears, and whether it is light or dark.
 *
 * The swatches are the real palette rather than a picture of one: each carries
 * `data-theme` (and the mode, when a mode has been chosen), so the variables
 * painting the preview are the same variables that would paint the app. That
 * also makes the preview show the theme's corner radius, which is half of what
 * separates Broadsheet from Market. A table of hex values copied into this file
 * would have drifted the first time styles.css changed.
 */
function AppearanceSettings() {
  const [theme, setTheme] = useState(readTheme);
  const [mode, setMode] = useState(readMode);

  const choose = (nextTheme, nextMode) => {
    setTheme(nextTheme);
    setMode(nextMode);
    applyTheme(nextTheme, nextMode);
  };

  return (
    <section>
      <div className="section-title"><h2>Appearance</h2></div>
      <div className="card stack">
        <div className="chips" role="group" aria-label="Light or dark">
          {MODES.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${mode === option.id ? ' active' : ''}`}
              aria-pressed={mode === option.id}
              onClick={() => choose(theme, option.id)}
            >
              {option.name}
            </button>
          ))}
        </div>

        <div className="stack">
          {THEMES.map((option) => (
            <button
              key={option.id}
              type="button"
              className="theme-option"
              aria-pressed={theme === option.id}
              onClick={() => choose(option.id, mode)}
            >
              <span
                className="theme-swatch"
                data-theme={option.id}
                /* Undefined omits the attribute, which is how "match device"
                   lets the preview follow the OS exactly as the app does. */
                data-mode={mode === 'system' ? undefined : mode}
                aria-hidden="true"
              >
                <span className="theme-swatch-accent" />
              </span>
              <span className="grow">
                {option.name}
                <span className="small muted" style={{ display: 'block', fontWeight: 400 }}>
                  {option.note}
                </span>
              </span>
              {theme === option.id && <Pill tone="ok">on</Pill>}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function PrivacySettings() {
  const privacy = useApi('/privacy');
  const { busy, error, setError, run } = useAction();
  const [confirm, setConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const download = () => run(async () => {
    setError(null);
    // Fetched rather than linked, because the endpoint needs the bearer token
    // that a plain <a href> would not carry.
    const blob = await api.blob('/privacy/export');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pantry-export-${todayLocal()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  });

  const optOut = () => run(async () => {
    setError(null);
    await api.post('/privacy/do-not-sell', {});
    await privacy.reload();
  });

  const destroy = () => run(async () => {
    setError(null);
    await api.post('/privacy/delete', { confirm: 'DELETE' });
    // Nothing left to be signed in to.
    await signOut();
    window.location.assign('/');
  });

  const data = privacy.data;

  return (
    <section>
      <div className="section-title"><h2>Your data</h2></div>
      {privacy.loading && <Loading />}
      {error && <ErrorNote error={error} />}

      <div className="card stack">
        <div>
          <strong>Download everything</strong>
          <p className="small muted" style={{ marginTop: 4 }}>
            One file with every row this household holds — inventory, receipts, recipes,
            lists, plans — plus who can reach it and where its photographs are stored.
          </p>
        </div>
        <button type="button" disabled={busy} onClick={download}>Download my data</button>
      </div>

      <div className="card stack" style={{ marginTop: 8 }}>
        <div>
          <strong>Do not sell or share my personal information</strong>
          <p className="small muted" style={{ marginTop: 4 }}>
            {data?.sells_personal_information === false && data?.shares_for_advertising === false
              ? 'Nothing is sold or shared for advertising, so there is currently nothing to opt out of. Recording it anyway means it already applies if that ever changes.'
              : 'Advertising is shown on the free plan, and opting out makes it non-personalised. It does not remove the advertising, and nothing else about the app changes.'}
          </p>
        </div>
        {data?.do_not_sell
          ? (
            <div className="alert ok" role="status">
              <strong>Recorded.</strong>
              <div className="small" style={{ marginTop: 4 }}>
                Opted out on {String(data.do_not_sell_at).slice(0, 10)}. Your browser may have
                sent this automatically — Global Privacy Control is honoured here.
              </div>
            </div>
          )
          : <button type="button" disabled={busy} onClick={optOut}>Opt out</button>}
      </div>

      <div className="card stack" style={{ marginTop: 8 }}>
        <div>
          <strong>Delete everything</strong>
          <p className="small muted" style={{ marginTop: 4 }}>
            Removes this household and every row in it, deletes the stored photographs, and
            cancels any subscription. It cannot be undone and there is no copy —
            download your data first if you want one.
          </p>
        </div>
        {!deleting
          ? (
            <button type="button" className="danger" disabled={busy} onClick={() => setDeleting(true)}>
              Delete my account and data
            </button>
          )
          : (
            <div className="stack">
              <label className="small" htmlFor="confirm-delete">
                Type <strong>DELETE</strong> to confirm.
              </label>
              <input
                id="confirm-delete"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="danger"
                  disabled={busy || confirm.trim().toUpperCase() !== 'DELETE'}
                  onClick={destroy}
                >
                  {busy ? <Spinner /> : null}
                  Permanently delete
                </button>
                <button type="button" onClick={() => { setDeleting(false); setConfirm(''); }}>
                  Keep my account
                </button>
              </div>
            </div>
          )}
      </div>
    </section>
  );
}

/**
 * Where the written explanation of the app lives.
 *
 * Both rows go outward rather than opening something here. The manual is a
 * public page -- it is the same URL somebody reads before signing up -- and the
 * tour is the wizard from the first sign-in, which is worth being able to reach
 * again: it is marked seen per person, so re-running it changes nothing except
 * showing it.
 */
function HelpSettings() {
  return (
    <section>
      <div className="section-title"><h2>Help</h2></div>
      <div className="card list">
        <Link className="list-item" to="/help" style={{ color: 'inherit', textDecoration: 'none' }}>
          <span className="grow">
            User manual
            <span className="small muted" style={{ display: 'block' }}>
              What every screen does, in writing.
            </span>
          </span>
        </Link>
        <Link className="list-item" to="/welcome" style={{ color: 'inherit', textDecoration: 'none' }}>
          <span className="grow">
            Take the tour again
            <span className="small muted" style={{ display: 'block' }}>
              The short introduction from your first sign-in.
            </span>
          </span>
        </Link>
      </div>
    </section>
  );
}

function AccountSettings() {
  const auth = useAuth();
  const { busy, error, run } = useAction();

  if (auth.status !== 'signed-in') return null;

  return (
    <section>
      <div className="section-title"><h2>Account</h2></div>
      <div className="card stack">
        <div className="list-item">
          <span className="grow truncate">{auth.user?.email || 'Signed in'}</span>
          {auth.user?.app_metadata?.provider && (
            <Pill>{auth.user.app_metadata.provider}</Pill>
          )}
        </div>
        <ErrorNote error={error} />
        <button type="button" className="full" disabled={busy} onClick={() => run(signOut)}>
          Sign out
        </button>
      </div>
    </section>
  );
}

/**
 * Who is in this household, and how somebody else gets in.
 *
 * The premise of the app is a shared pantry, so this is not an advanced
 * setting -- it sits directly under Account, because "add my partner" is a
 * first-week action rather than a power-user one.
 *
 * Owner-only controls are hidden rather than shown and refused. A member who
 * cannot invite does not need a button that explains that to them; the server
 * refuses either way, and this is the display half of the same rule.
 */
function HouseholdSettings() {
  const members = useApi('/household/members');
  const invites = useApi('/household/invites');
  const [inviting, setInviting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const { busy, error, run } = useAction();

  const data = members.data;
  const owner = data?.you_are_owner === true;
  const you = data?.members?.find((m) => m.is_you);

  const reload = () => { members.reload(); invites.reload(); };

  const remove = (member) => run(async () => {
    const leaving = member.is_you;
    const ok = window.confirm(leaving
      ? 'Leave this household? You will lose access to everything in it.'
      : `Remove ${member.display_name || member.email} from this household?`);
    if (!ok) return;
    await api.delete(`/household/members/${member.id}`);
    // Leaving takes away the page you are standing on, so start over rather
    // than re-render a household you are no longer in.
    if (leaving) window.location.assign('/');
    else reload();
  });

  const changeRole = (member) => run(async () => {
    await api.patch(`/household/members/${member.id}`, {
      role: member.role === 'owner' ? 'member' : 'owner',
    });
    reload();
  });

  const revoke = (invite) => run(async () => {
    await api.delete(`/household/invites/${invite.id}`);
    invites.reload();
  });

  return (
    <section>
      <div className="section-title"><h2>Household</h2></div>
      <div className="card stack">
        {members.loading && !data && <Loading rows={2} />}
        {members.error && <ErrorNote error={members.error} onRetry={members.reload} />}
        <ErrorNote error={error} />

        {data?.members?.map((member) => (
          <div className="list-item" key={member.id}>
            <span className="grow truncate">
              {member.display_name || member.email || 'Someone'}
              {member.is_you && <span className="small muted"> &mdash; you</span>}
              {member.display_name && member.email && (
                <span className="small muted truncate" style={{ display: 'block' }}>{member.email}</span>
              )}
            </span>
            {member.role === 'owner' && <Pill tone="ok">Owner</Pill>}
            {owner && !member.is_you && (
              <button type="button" className="link small" disabled={busy} onClick={() => changeRole(member)}>
                {member.role === 'owner' ? 'Make member' : 'Make owner'}
              </button>
            )}
            {(owner || member.is_you) && (
              <button type="button" className="link small" disabled={busy} onClick={() => remove(member)}>
                {member.is_you ? 'Leave' : 'Remove'}
              </button>
            )}
          </div>
        ))}

        <div className="row wrap" style={{ gap: 10 }}>
          {owner && (
            <button type="button" className="grow" disabled={busy} onClick={() => setInviting(true)}>
              Invite someone
            </button>
          )}
          {you && (
            <button type="button" className="grow" disabled={busy} onClick={() => setRenaming(true)}>
              Change your name
            </button>
          )}
        </div>

        {owner && invites.data?.invites?.length > 0 && (
          <>
            <hr />
            <span className="small muted">Invitations waiting to be accepted</span>
            {invites.data.invites.map((invite) => (
              <div className="list-item" key={invite.id}>
                <span className="grow">
                  <span className="mono">{invite.code}</span>
                  {invite.email && (
                    <span className="small muted truncate" style={{ display: 'block' }}>{invite.email}</span>
                  )}
                </span>
                <span className="small muted">expires {String(invite.expires_at).slice(0, 10)}</span>
                <button type="button" className="link small" disabled={busy} onClick={() => revoke(invite)}>
                  Revoke
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {inviting && <InviteSomeone onClose={() => { setInviting(false); invites.reload(); }} />}
      {renaming && (
        <ChangeYourName
          current={you?.display_name || ''}
          onClose={() => setRenaming(false)}
          onSaved={() => { setRenaming(false); members.reload(); }}
        />
      )}
    </section>
  );
}

/**
 * Mint an invitation and show it exactly once.
 *
 * The token is stored as a hash and cannot be read back, so this modal is the
 * only place it will ever exist. Said out loud rather than left implicit,
 * because somebody who closes it expecting to find the link later has lost
 * nothing except the ten seconds it takes to make another.
 */
function InviteSomeone({ onClose }) {
  const [email, setEmail] = useState('');
  const [invite, setInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const { busy, error, run } = useAction();

  const link = invite
    ? `${window.location.origin}/join?token=${encodeURIComponent(invite.token)}`
    : '';

  const create = () => run(async () => {
    setInvite(await api.post('/household/invites', { email: email.trim() || undefined }));
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch { /* a browser that refuses the clipboard still shows the link */ }
  };

  return (
    <Modal
      title={invite ? 'Send this to them' : 'Invite someone'}
      onClose={onClose}
      footer={invite
        ? <button type="button" className="primary" onClick={onClose}>Done</button>
        : (
          <>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" className="primary" disabled={busy} onClick={create}>
              Create invitation
            </button>
          </>
        )}
    >
      <ErrorNote error={error} />
      {invite ? (
        <div className="stack">
          {invite.emailed && (
            <div className="alert info small">
              Emailed to <strong>{invite.email}</strong>. The link below works too, if it does
              not arrive.
            </div>
          )}
          {invite.email_note && (
            <div className="alert warn small">{invite.email_note}</div>
          )}
          <p className="small">
            This link works once and expires in {invite.expires_in_days} days. It is shown here
            only &mdash; it is not kept anywhere it can be read back, so make another if it gets
            lost.
          </p>
          <input className="mono" readOnly value={link} onFocus={(e) => e.target.select()} />
          <button type="button" className="full" onClick={copy}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <p className="small muted" style={{ marginBottom: 0 }}>Or read them this code:</p>
          <strong className="code-callout" aria-label={invite.code.split('').join(' ')}>
            {invite.code}
          </strong>
          <p className="small muted" style={{ marginTop: 0 }}>
            They can type it on the screen they see after signing up.
          </p>
        </div>
      ) : (
        <div className="stack">
          <p className="small">
            They will need an account of their own. Anyone holding the link or the code can join
            this household, so send it the way you would send a key.
          </p>
          <label className="small muted" htmlFor="invite-email">
            Their email (optional). Given one, the invitation is emailed to them &mdash; and if
            they sign up with that address it is offered to them even without the code. You will
            be told either way, and the link and code work regardless.
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            placeholder="them@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}

/** The name activity is attributed to, which is why it belongs to the person named. */
function ChangeYourName({ current, onClose, onSaved }) {
  const [name, setName] = useState(current);
  const { busy, error, run } = useAction();

  return (
    <Modal
      title="Change your name"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="primary"
            disabled={busy || !name.trim()}
            onClick={() => run(async () => {
              await api.patch('/household/me', { display_name: name.trim() });
              onSaved();
            })}
          >
            Save
          </button>
        </>
      )}
    >
      <ErrorNote error={error} />
      <p className="small">This is how you appear to everyone else in the household.</p>
      <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
    </Modal>
  );
}

/** Req #6: add / edit / delete storage locations. */
function LocationSettings() {
  const locations = useApi('/locations?includeArchived=true');
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const { busy, error, run } = useAction();

  const remove = (location) => run(async () => {
    const hasItems = location.item_count > 0;
    const message = hasItems
      ? `“${location.name}” holds ${location.item_count} item(s). Archive it? The items stay put and the location stops appearing in pickers.`
      : `Delete “${location.name}”?`;
    if (!window.confirm(message)) return;
    await api.delete(`/locations/${location.id}${hasItems ? '' : '?force=true'}`);
    locations.reload();
  });

  const restore = (location) => run(async () => {
    await api.post(`/locations/${location.id}/restore`, {});
    locations.reload();
  });

  const active = (locations.data || []).filter((l) => !l.archived_at);
  const archived = (locations.data || []).filter((l) => l.archived_at);

  return (
    <section>
      <div className="section-title">
        <h2>Storage locations</h2>
        <button type="button" className="small ghost" onClick={() => setAdding(true)}>+ add</button>
      </div>
      <ErrorNote error={error} />
      {locations.loading && !locations.data && <Loading rows={2} />}

      <div className="card list">
        {active.map((location) => (
          <div key={location.id} className="list-item">
            <button
              type="button"
              className="ghost grow"
              style={{ justifyContent: 'flex-start', textAlign: 'left', padding: 0, minHeight: 0 }}
              onClick={() => setEditing(location)}
            >
              <span className="grow" style={{ minWidth: 0 }}>
                <span className="truncate" style={{ display: 'block' }}>{location.name}</span>
                <span className="tiny muted">{location.kind} · {location.item_count} items</span>
              </span>
            </button>
            <button type="button" className="icon ghost" aria-label={`Delete ${location.name}`}
              disabled={busy} onClick={() => remove(location)}>✕</button>
          </div>
        ))}
      </div>

      {archived.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="small muted">{archived.length} archived</summary>
          <div className="card list" style={{ marginTop: 8 }}>
            {archived.map((location) => (
              <div key={location.id} className="list-item">
                <span className="grow truncate muted">{location.name}</span>
                <button type="button" className="small" disabled={busy} onClick={() => restore(location)}>
                  Restore
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      {(adding || editing) && (
        <LocationForm
          location={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); locations.reload(); }}
        />
      )}
    </section>
  );
}

function LocationForm({ location, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: location?.name || '',
    kind: location?.kind || 'other',
    description: location?.description || '',
    sort_order: location?.sort_order ?? 0,
  });
  const { busy, error, run } = useAction();
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const save = () => run(async () => {
    if (location) await api.patch(`/locations/${location.id}`, { ...form, sort_order: Number(form.sort_order) });
    else await api.post('/locations', { ...form, sort_order: Number(form.sort_order) });
    onSaved();
  });

  const KINDS = ['refrigerator', 'freezer', 'pantry', 'cabinet', 'spice', 'cellar', 'other'];

  return (
    <Modal title={location ? 'Edit location' : 'New location'} onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />
        <label><span className="label-text">Name</span>
          <input value={form.name} onChange={set('name')} placeholder="Garage refrigerator" autoFocus /></label>
        <label><span className="label-text">Kind</span>
          <select value={form.kind} onChange={set('kind')}>
            {KINDS.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
          </select>
        </label>
        <label><span className="label-text">Description</span>
          <input value={form.description} onChange={set('description')} placeholder="Optional" /></label>
        <label><span className="label-text">Sort order</span>
          <input type="number" inputMode="numeric" value={form.sort_order} onChange={set('sort_order')} /></label>
        <button type="button" className="primary full" disabled={busy || !form.name.trim()} onClick={save}>
          {busy ? <><span className="spinner" /> Saving</> : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Receipts by email: the household's own address, and what has arrived at it.
 *
 * Renders nothing where the deployment does not receive mail, which is the
 * normal state on a laptop -- an address that goes nowhere would be worse than
 * no card. The address is shown in full and copied in one tap, because it is
 * only ever typed once, into a mail client's forwarding rule or a contact.
 */
const INBOUND_STATUS = {
  received: ['Reading', null],
  processed: ['Added to review', 'ok'],
  no_content: ['Nothing readable in it', 'warn'],
  quota: ['Out of scans', 'warn'],
  failed: ['Could not read it', 'warn'],
};

function EmailReceiptsCard() {
  const health = useApi('/health');
  const enabled = health.data?.inboundEmail === true;
  const inbox = useApi(enabled ? '/inbound/address' : null);
  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const { busy, error, run } = useAction();

  if (!enabled) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inbox.data.address);
      setCopied(true);
    } catch { /* a browser that refuses the clipboard still shows the address */ }
  };

  const rotate = () => run(async () => {
    await api.post('/inbound/address/rotate');
    setConfirmRotate(false);
    setCopied(false);
    inbox.reload();
  });

  return (
    <>
      <div className="card stack">
        <p className="small" style={{ marginTop: 0 }}>
          Forward a store&rsquo;s order confirmation or digital receipt here, or attach a photo of
          a paper one. It appears under <em>Receipts to review</em> and costs one scan, like a
          photograph.
        </p>
        <ErrorNote error={inbox.error || error} />
        {inbox.data && (
          <>
            <input className="mono" readOnly value={inbox.data.address} onFocus={(e) => e.target.select()} />
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="grow" onClick={copy}>{copied ? 'Copied' : 'Copy address'}</button>
              <button type="button" disabled={busy} onClick={() => setConfirmRotate(true)}>New address</button>
            </div>
            <p className="tiny muted" style={{ margin: 0 }}>
              Anyone who has this address can send you a receipt to review, and nothing more. If it
              gets around, <em>New address</em> replaces it and the old one stops working.
            </p>
          </>
        )}
      </div>

      {inbox.data?.recent?.length > 0 && (
        <div className="card list" style={{ marginTop: 8 }}>
          {inbox.data.recent.map((row) => {
            const [label, tone] = INBOUND_STATUS[row.status] || [row.status, null];
            const first = row.purchase_ids?.[0];
            return (
              <div className="list-item" key={row.id}>
                <div className="grow">
                  <div className="small">{row.subject || '(no subject)'}</div>
                  <div className="tiny muted">
                    {row.from_address || 'unknown sender'} &middot; {String(row.created_at).slice(0, 10)}
                    {row.error ? ` · ${row.error}` : ''}
                  </div>
                </div>
                {first && row.status === 'processed'
                  ? <Link className="small" to={`/receipts/${first}`}>{label}</Link>
                  : <Pill tone={tone || undefined}>{label}</Pill>}
              </div>
            );
          })}
        </div>
      )}

      {confirmRotate && (
        <Modal
          title="Replace the address?"
          onClose={() => setConfirmRotate(false)}
          footer={(
            <>
              <button type="button" onClick={() => setConfirmRotate(false)}>Keep it</button>
              <button type="button" className="primary" disabled={busy} onClick={rotate}>
                {busy ? <Spinner /> : 'Replace it'}
              </button>
            </>
          )}
        >
          <p className="small">
            Anything already set up to forward to the current address &mdash; a rule in your mail,
            a saved contact &mdash; will need the new one. Mail to the old address is dropped.
          </p>
        </Modal>
      )}
    </>
  );
}

/** Req #2: purchase-history import. */
function ImportSettings() {
  const stores = useApi('/imports/stores');
  const [storeId, setStoreId] = useState('');
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);
  const { busy, error, run } = useAction();

  const upload = (file) => run(async () => {
    const form = new FormData();
    form.append('file', file);
    if (storeId) form.append('store_id', storeId);
    const response = await api.upload('/imports/csv', form);
    setResult(response);
    stores.reload();
  });

  return (
    <section>
      <div className="section-title"><h2>Import purchase history</h2></div>

      <EmailReceiptsCard />

      <ErrorNote error={error} />

      <div className="card stack" style={{ marginTop: 8 }}>
        <label>
          <span className="label-text">Store</span>
          <select value={storeId} onChange={(event) => setStoreId(event.target.value)}>
            <option value="">Unspecified</option>
            {(stores.data?.stores || []).map((store) => (
              <option key={store.id} value={store.id}>{store.name} ({store.purchase_count})</option>
            ))}
          </select>
        </label>

        <input
          ref={fileInput}
          className="photo-input"
            tabIndex={-1}
            aria-hidden="true"
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) upload(file);
          }}
        />
        <button type="button" className="primary full" disabled={busy} onClick={() => fileInput.current?.click()}>
          {busy ? <><span className="spinner" /> Importing</> : 'Upload a CSV'}
        </button>

        {result && (
          <div className="alert info small">
            Imported {result.imported} order{result.imported === 1 ? '' : 's'} ({result.items} lines,
            {' '}{result.newItems} new products). Skipped {result.skipped} already imported.
            <div style={{ marginTop: 6 }}>{result.note}</div>
          </div>
        )}

        <p className="tiny muted" style={{ margin: 0 }}>
          Columns recognized: date, item, quantity, unit, price, total, order id. Re-importing an
          overlapping range is safe — nothing is duplicated.
        </p>
      </div>
    </section>
  );
}

/** Req #12: which sites recipe discovery is pointed at. */
function RecipeSourceSettings() {
  const sources = useApi('/recipes/sources/list');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const { busy, error, run } = useAction();

  const add = () => run(async () => {
    await api.post('/recipes/sources/list', { name: name.trim(), domain: domain.trim() });
    setName('');
    setDomain('');
    sources.reload();
  });

  const toggle = (source) => run(async () => {
    await api.patch(`/recipes/sources/list/${source.id}`, { enabled: source.enabled ? 0 : 1 });
    sources.reload();
  });

  const remove = (source) => run(async () => {
    await api.delete(`/recipes/sources/list/${source.id}`);
    sources.reload();
  });

  return (
    <section>
      <div className="section-title"><h2>Recipe sources</h2></div>
      <ErrorNote error={error} />

      <div className="card list">
        {(sources.data?.sources || []).map((source) => (
          <div key={source.id} className="list-item">
            <label className="checkbox grow" style={{ minWidth: 0 }}>
              <input type="checkbox" checked={Boolean(source.enabled)} onChange={() => toggle(source)} disabled={busy} />
              <span className="grow" style={{ minWidth: 0 }}>
                <span className="truncate" style={{ display: 'block' }}>{source.name}</span>
                <span className="tiny muted">{source.domain}</span>
              </span>
            </label>
            <button type="button" className="icon ghost" aria-label={`Remove ${source.name}`}
              disabled={busy} onClick={() => remove(source)}>✕</button>
          </div>
        ))}
      </div>

      <div className="card stack" style={{ marginTop: 10 }}>
        <div className="field-row">
          <label><span className="label-text">Name</span>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Bon Appétit" /></label>
          <label><span className="label-text">Domain</span>
            <input value={domain} onChange={(event) => setDomain(event.target.value)} placeholder="bonappetit.com" /></label>
        </div>
        <button type="button" className="full" disabled={busy || !name.trim() || !domain.trim()} onClick={add}>
          Add source
        </button>
      </div>

      <p className="tiny muted" style={{ marginTop: 8 }}>
        These are the sites the app links out to for discovery. Recipes are only stored when you
        import one, and importing reads the page&rsquo;s own published recipe data rather than scraping
        the catalogue.
      </p>
    </section>
  );
}
